import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { readPreference, savePreference } from "@/lib/tabPreferences";
import { cancelDeviceSpeech, speakReply } from "./deviceSpeech";

const EMPTY = { messageId: null, status: "idle", error: "", part: 0, parts: 1, progress: 0 };

export const useSpeechPlayer = (open) => {
  const { user, loading } = useAuth();
  const supported = typeof window.speechSynthesis !== "undefined" && typeof window.SpeechSynthesisUtterance !== "undefined";
  const [voices, setVoices] = useState([]), [state, setState] = useState(EMPTY);
  const [voice, setVoice] = useState(() => readPreference("kandula-device-voice") || "auto");
  const [speed, setSpeed] = useState(1);
  const runtime = useRef({ generation: 0, utterance: null, synth: null });
  const selectedVoice = voices.some((item) => item.voiceURI === voice) ? voice : "auto";
  const refreshVoices = useCallback(() => {
    if (supported) setVoices(window.speechSynthesis.getVoices().filter((item) => item.localService === true));
  }, [supported]);
  useEffect(() => {
    if (!supported) return undefined;
    const synth = window.speechSynthesis;
    synth.addEventListener("voiceschanged", refreshVoices); refreshVoices();
    return () => synth.removeEventListener("voiceschanged", refreshVoices);
  }, [supported, refreshVoices]);
  const stop = useCallback(() => { cancelDeviceSpeech(runtime.current); setState(EMPTY); }, []);
  useEffect(() => {
    const active = runtime.current;
    stop();
    const hidden = () => { if (document.hidden) stop(); };
    document.addEventListener("visibilitychange", hidden);
    return () => { document.removeEventListener("visibilitychange", hidden); cancelDeviceSpeech(active); };
  }, [open, user?.user_id, loading, stop]);
  const chooseVoice = (value) => {
    if (value !== "auto" && !voices.some((item) => item.voiceURI === value)) return;
    stop(); setVoice(value); savePreference("kandula-device-voice", value);
  };
  const listen = (message) => {
    const active = runtime.current;
    if (state.messageId === message.id && active.utterance) {
      if (state.status === "playing") {
        active.synth.pause(); setState((old) => ({ ...old, status: "paused" })); return;
      }
      if (state.status === "paused") {
        active.synth.resume(); setState((old) => ({ ...old, status: "playing" })); return;
      }
    }
    speakReply(active, message, { voice: selectedVoice, speed }, setState);
  };
  const changeSpeed = (value) => {
    if (![0.75, 1, 1.25, 1.5].includes(value)) return;
    stop(); setSpeed(value);
  };
  return { ...state, voices, supported, voice: selectedVoice, speed, chooseVoice, listen, stop, changeSpeed, refreshVoices };
};